import React, { useState, useEffect } from 'react';
import { db, auth, storage } from '../firebaseConfig';
import { 
    collection, 
    addDoc, 
    getDocs,
    getDoc,
    doc, 
    Timestamp,
    serverTimestamp,
    query,
    where,
    updateDoc,
    increment,
    setDoc,
    runTransaction
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import deepai from 'deepai';
import AdGatedFeatures from './AdGatedFeatures';
import { FaMicrophone, FaStop, FaImage, FaMagic, FaQuestionCircle } from 'react-icons/fa';
import { calculateFeatureCost } from '../utils/stripe';

// Set your API key
deepai.setApiKey(process.env.REACT_APP_DEEPAI_API_KEY);

// Constants
const ART_STYLES = [
    { name: 'fantasy-world-generator', label: 'Fantasy World' },
    { name: 'text2img', label: 'Realistic' },
    { name: 'abstract-painting-generator', label: 'Abstract Painting' },
    { name: 'impressionism-painting-generator', label: 'Impressionism' },
    { name: 'steampunk-generator', label: 'Steampunk' },
    { name: 'dystopian-landscape-generator', label: 'Dystopian' },
    { name: 'surreal-graphics-generator', label: 'Surreal' },
    { name: 'prophetic-vision-generator', label: 'Prophetic' },
    { name: 'tribal-art-generator', label: 'Tribal' },
    { name: 'mysticism-art-generator', label: 'Mysticism' },
    { name: 'steampunk-landscape-generator', label: 'Steampunk Landscape' },
    { name: 'art-nouveau-generator', label: 'Art Nouveau' },
    { name: 'dreamscape-generator', label: 'Dreamscape' },
    { name: 'fairy-tale-art-generator', label: 'Fairy Tale' },
    { name: 'psychedelic-poster-generator', label: 'Psychedelic' },
    { name: 'unreal-cityscape-generator', label: 'Unreal Cityscape' },
    { name: 'dystopian-cyberpunk-generator', label: 'Dystopian Cyberpunk' }
];

export const generateDreamImage = async (content, selectedStyle) => {
    try {
        // Validate inputs
        if (!content || !selectedStyle) {
            throw new Error('Content and style are required for image generation');
        }

        // Ensure API key is set
        const apiKey = process.env.REACT_APP_DEEPAI_API_KEY;
        if (!apiKey) {
            throw new Error('DeepAI API Key is not configured');
        }

        // Create a more detailed, context-rich prompt
        const dreamPrompt = `A surreal, dreamlike ${selectedStyle.label.toLowerCase()} scene depicting: ${content}. 
        Emphasize ethereal, mystical atmosphere with soft, dreamy lighting, floating elements, 
        and a sense of wonder and imagination. Use vibrant yet slightly blurred colors to 
        enhance the dream-like quality.`;

        // Add more robust error handling
        try {
            const resp = await deepai.callStandardApi(selectedStyle.name, {
                text: dreamPrompt,
            });
            
            if (!resp || !resp.output_url) {
                throw new Error('No image generated by DeepAI');
            }

            // Log the generated image URL for debugging
            console.log('DeepAI Generated Image URL:', resp.output_url);

            // Validate image URL
            const imageUrl = new URL(resp.output_url);
            
            return resp.output_url;
        } catch (apiError) {
            console.error('DeepAI API Detailed Error:', {
                message: apiError.message,
                name: apiError.name,
                stack: apiError.stack
            });

            throw new Error(`Image Generation Failed: ${apiError.message}`);
        }
    } catch (error) {
        console.error('Dream Image Generation Error:', {
            message: error.message,
            stack: error.stack
        });

        // Provide more context in the error
        throw new Error(`Dream Image Generation Failed: ${error.message}`);
    }
};

export const saveGeneratedImageToStorage = async (imageUrl, userId) => {
    try {
        // Ensure user is authenticated
        if (!auth.currentUser || auth.currentUser.uid !== userId) {
            throw new Error('User authentication failed');
        }

        // Validate input URL
        if (!imageUrl) {
            throw new Error('No image URL provided');
        }

        // Create an Image object to validate and load the image
        const img = new Image();
        img.crossOrigin = 'Anonymous';

        const imageLoadPromise = new Promise((resolve, reject) => {
            img.onload = () => {
                // Create canvas to ensure image is valid and can be converted
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);

                // Convert to blob
                canvas.toBlob(async (blob) => {
                    if (!blob) {
                        reject(new Error('Failed to convert image to blob'));
                        return;
                    }

                    try {
                        // Generate a more unique filename with additional security
                        const filename = `dream_${userId}_${Date.now()}_${Math.random().toString(36).substring(2, 15)}.png`;
                        const imageRef = ref(storage, `dream_images/${userId}/${filename}`);

                        // Enhanced metadata with additional security checks
                        const metadata = {
                            contentType: 'image/png',
                            customMetadata: {
                                'generatedBy': 'DreamAI',
                                'userId': userId,
                                'timestamp': Date.now().toString(),
                                'originalUrl': imageUrl,
                                'width': img.width.toString(),
                                'height': img.height.toString()
                            }
                        };

                        // Upload the blob
                        const uploadResult = await uploadBytes(imageRef, blob, metadata);
                        
                        // Get download URL with cache-busting
                        const storedImageUrl = await getDownloadURL(uploadResult.ref);
                        
                        // Validate the stored image URL
                        const validatedUrl = await new Promise((resolveUrl, rejectUrl) => {
                            const testImg = new Image();
                            testImg.onload = () => resolveUrl(storedImageUrl);
                            testImg.onerror = () => rejectUrl(new Error('Invalid stored image URL'));
                            testImg.src = storedImageUrl;
                        });

                        resolve(validatedUrl);
                    } catch (uploadError) {
                        console.error('Firebase Upload Error:', {
                            message: uploadError.message,
                            code: uploadError.code,
                            userId: userId
                        });

                        // More specific error handling for Firebase upload
                        if (uploadError.code === 'storage/unauthorized') {
                            reject(new Error('Not authorized to upload image. Check Firebase Storage rules.'));
                        } else {
                            reject(uploadError);
                        }
                    }
                }, 'image/png');
            };

            img.onerror = () => {
                reject(new Error(`Failed to load image from URL: ${imageUrl}`));
            };

            // Set src to trigger loading
            img.src = imageUrl;
        });

        // Wait for image processing and return the validated URL
        return await imageLoadPromise;

    } catch (error) {
        console.error('Comprehensive Image Storage Error:', {
            message: error.message,
            name: error.name,
            stack: error.stack,
            url: imageUrl,
            userId: userId
        });

        // Rethrow the error for upstream handling
        throw error;
    }
};

const ImageGenerationToggle = ({ 
    isChecked, 
    onToggle, 
    userCredits, 
    userRole 
}) => {
    const [showCostTooltip, setShowCostTooltip] = useState(false);
    const imageCost = userRole === 'pro' ? 0.05 : 0.10;

    return (
        <div className="relative group">
            <div 
                className={`
                    flex items-center space-x-2 cursor-pointer 
                    p-2 rounded-lg transition-all duration-300 ease-in-out
                    ${isChecked 
                        ? 'bg-purple-600/20 border-purple-500/50' 
                        : 'bg-white/10 border-transparent'}
                    border hover:bg-purple-600/30
                `}
                onClick={onToggle}
            >
                <div 
                    className={`
                        w-6 h-6 rounded-md flex items-center justify-center 
                        transition-all duration-300 ease-in-out
                        ${isChecked 
                            ? 'bg-purple-600 text-white' 
                            : 'bg-white/20 text-purple-300'}
                    `}
                >
                    {isChecked ? <FaImage className="w-4 h-4" /> : <FaMagic className="w-4 h-4" />}
                </div>
                <span className="text-sm text-white/80">
                    Generate Dream Image
                </span>
                <FaQuestionCircle 
                    className="text-purple-300 hover:text-purple-200 ml-1"
                    onMouseEnter={() => setShowCostTooltip(true)}
                    onMouseLeave={() => setShowCostTooltip(false)}
                />
            </div>

            {showCostTooltip && (
                <div className="absolute z-10 bottom-full left-0 mb-2 
                    bg-black/80 text-white text-xs rounded-lg 
                    p-2 w-64 shadow-lg"
                >
                    <div className="flex items-center space-x-2">
                        <FaMagic className="text-purple-400" />
                        <span>Image Generation Cost</span>
                    </div>
                    <div className="mt-1">
                        <p>1 Credit per Image</p>
                        <p className="text-[10px] text-gray-400">
                            Current Credits: {userCredits}
                        </p>
                        {userRole === 'pro' && (
                            <p className="text-green-300 text-[10px] mt-1">
                                Pro User: 50% Off!
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const PostForm = ({ onAddPost }) => {
    const [content, setContent] = useState('');
    const [tags, setTags] = useState([]);
    const [mentions, setMentions] = useState([]);
    const [tagSuggestions, setTagSuggestions] = useState([]);
    const [mentionSuggestions, setMentionSuggestions] = useState([]);
    const [userType, setUserType] = useState('free');
    const [loading, setLoading] = useState(false);
    const [visibility, setVisibility] = useState('public');
    const [isRecording, setIsRecording] = useState(false);
    const [recognition, setRecognition] = useState(null);
    const [artStyle, setArtStyle] = useState('');
    const [userData, setUserData] = useState({
        credits: 0,
        role: 'simple'
    });
    const [features, setFeatures] = useState({
        imageGeneration: false,
        dreamInterpretation: false
    });

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    // Get or create profile
                    const profileRef = doc(db, 'profiles', user.uid);
                    const profileDoc = await getDoc(profileRef);
                    
                    if (!profileDoc.exists()) {
                        // Create new profile if it doesn't exist
                        await setDoc(profileRef, {
                            userId: user.uid,
                            username: user.displayName || 'Anonymous',
                            role: 'simple',
                            photoURL: user.photoURL || '/logo.png',
                            subscriptionStatus: {
                                type: 'free',
                                endDate: null
                            },
                            stats: {
                                totalDreams: 0
                            },
                            createdAt: Timestamp.now()
                        });
                    }
                    
                    // Get profile data
                    const userProfileData = profileDoc.exists() ? profileDoc.data() : {};
                    setUserType(userProfileData.subscriptionStatus?.type || 'free');
                    
                    // Update userData with profile information
                    setUserData({
                        credits: userProfileData.credits || 0,
                        role: userProfileData.role || 'simple'
                    });
                } catch (error) {
                    console.error('Error fetching/creating profile:', error);
                }
            }
        });

        return () => unsubscribe();
    }, []);

    // Fetch existing tags and mentions from previous posts
  useEffect(() => {
    const fetchExistingTagsAndMentions = async () => {
      try {
        const postsQuery = query(collection(db, 'posts'));
        const querySnapshot = await getDocs(postsQuery);
        
        const existingTags = new Set();
        const existingMentions = new Set();

        querySnapshot.forEach((doc) => {
          const postData = doc.data();
          
          // Collect tags
          if (postData.tags) {
            postData.tags.forEach(tag => existingTags.add(tag));
          }

          // Collect mentions
          if (postData.mentions) {
            postData.mentions.forEach(mention => existingMentions.add(mention));
          }
        });

        setTagSuggestions(Array.from(existingTags));
        setMentionSuggestions(Array.from(existingMentions));
      } catch (error) {
        console.error('Error fetching existing tags and mentions:', error);
      }
    };

    fetchExistingTagsAndMentions();
  }, []);

  // Regex for detecting tags and mentions
  const TAG_REGEX = /#(\w+)/g;
  const MENTION_REGEX = /@(\w+)/g;

  const handleContentChange = (e) => {
    const text = e.target.value;
    setContent(text);

    // Extract tags
    const extractedTags = [...text.matchAll(TAG_REGEX)].map(match => match[1]);
    setTags([...new Set(extractedTags)]);

    // Extract mentions
    const extractedMentions = [...text.matchAll(MENTION_REGEX)].map(match => match[1]);
    setMentions([...new Set(extractedMentions)]);
  };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    // Get or create profile
                    const profileRef = doc(db, 'profiles', user.uid);
                    const profileDoc = await getDoc(profileRef);
                    
                    if (!profileDoc.exists()) {
                        // Create new profile if it doesn't exist
                        await setDoc(profileRef, {
                            userId: user.uid,
                            username: user.displayName || 'Anonymous',
                            role: 'simple',
                            photoURL: user.photoURL || '/logo.png',
                            subscriptionStatus: {
                                type: 'free',
                                endDate: null
                            },
                            stats: {
                                totalDreams: 0
                            },
                            createdAt: Timestamp.now()
                        });
                    }
                    
                    // Get profile data
                    const userData = profileDoc.exists() ? profileDoc.data() : {};
                    setUserType(userData.subscriptionStatus?.type || 'free');
                } catch (error) {
                    console.error('Error fetching/creating profile:', error);
                }
            }
        });

        return () => unsubscribe();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!auth.currentUser) {
            alert('You must be logged in to create a post.');
            return;
        }
    
        try {
            const userRef = doc(db, 'profiles', auth.currentUser.uid);
            const userDoc = await getDoc(userRef);
            
            if (!userDoc.exists()) {
                throw new Error('User profile not found');
            }
    
            const userData = userDoc.data();
    
            let generatedImageUrl = null;
            let imageCost = 0;
            let selectedStyle = null;
    
            // Image generation with enhanced validation and error handling
            if (features.imageGeneration && artStyle) {
                // Validate art style
                selectedStyle = ART_STYLES.find(style => style.name === artStyle);
                if (!selectedStyle) {
                    alert('Invalid art style selected.');
                    return;
                }
    
                // Cost calculation
                imageCost = userData.role === 'pro' ? 0.05 : 0.10;
    
                // Strict credit check
                if (!userData.credits || userData.credits < imageCost) {
                    alert('Insufficient credits for image generation.');
                    return;
                }
    
                try {
                    // Comprehensive image generation
                    const originalImageUrl = await generateDreamImage(content, selectedStyle);
                    
                    // Ensure image is successfully fetched and stored
                    generatedImageUrl = await saveGeneratedImageToStorage(
                        originalImageUrl, 
                        auth.currentUser.uid
                    );
    
                    // Log successful image generation
                    console.log('Generated Image URL:', generatedImageUrl);
                } catch (imageGenerationError) {
                    console.error('Comprehensive Image Generation Failure:', imageGenerationError);
                    alert(`Image generation failed: ${imageGenerationError.message}`);
                    
                    // Reset image generation flags if generation fails
                    features.imageGeneration = false;
                    selectedStyle = null;
                    generatedImageUrl = null;
                }
            }
    
            // Prepare transaction to update credits
            const creditTransaction = async (transaction) => {
                const userDocRef = doc(db, 'profiles', auth.currentUser.uid);
                const userSnapshot = await transaction.get(userDocRef);
                
                if (!userSnapshot.exists()) {
                    throw new Error('User document not found');
                }
    
                const currentCredits = userSnapshot.data().credits || 0;
                
                // Ensure sufficient credits before deducting
                if (currentCredits < imageCost) {
                    throw new Error('Insufficient credits');
                }
    
                // Deduct credits
                transaction.update(userDocRef, {
                    credits: increment(-imageCost)
                });
            };
    
            // Run the transaction
            await runTransaction(db, creditTransaction);
    
            // Proceed with post creation
            setLoading(true);
            
            const postData = {
                content,
                userId: auth.currentUser.uid,
                username: auth.currentUser.displayName || 'Anonymous',
                createdAt: serverTimestamp(),
                tags,
                mentions,
                visibility,
                likes: [],
                // Explicitly set image generation details
                imageGeneration: !!generatedImageUrl, // Convert to boolean
                ...(generatedImageUrl && { imageUrl: generatedImageUrl }),
                artStyle: selectedStyle ? selectedStyle.name : null,
                imageCost  // Store the cost of image generation
            };
    
            // Create post
            const docRef = await addDoc(collection(db, 'posts'), postData);
    
            // Reset form
            setContent('');
            setTags([]);
            setMentions([]);
            setFeatures({ imageGeneration: false, dreamInterpretation: false });
            setArtStyle('');
    
            // Optional: Notify user of successful post
            alert('Post created successfully!');
    
        } catch (error) {
            console.error('Post creation error:', error);
            
            // Provide user-friendly error messages
            if (error.message.includes('Insufficient credits')) {
                alert('You do not have enough credits to complete this action.');
            } else {
                alert('An error occurred while creating your post. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Function to insert a suggestion
  const insertSuggestion = (suggestion, type) => {
    const suggestionText = type === 'tag' ? `#${suggestion}` : `@${suggestion}`;
    setContent(prevContent => {
      // Replace the last partial tag/mention with the full suggestion
      const regex = type === 'tag' ? /#\w*$/ : /@\w*$/;
      return prevContent.replace(regex, suggestionText);
    });
  };

    const handleFeatureSelect = (selectedFeatures) => {
        setFeatures(selectedFeatures);
        if (!selectedFeatures.imageGeneration) {
            setArtStyle('');
        }
    };

    const toggleImageGeneration = () => {
        setFeatures(prev => ({
            ...prev, 
            imageGeneration: !prev.imageGeneration
        }));
    };

    useEffect(() => {
        if ('webkitSpeechRecognition' in window) {
          const recognition = new window.webkitSpeechRecognition();
          recognition.continuous = true;
          recognition.interimResults = true;
          recognition.lang = 'en-US';
    
          recognition.onresult = (event) => {
            const transcript = Array.from(event.results)
              .map(result => result[0].transcript)
              .join('');
            
            setContent(prevContent => {
              return prevContent + ' ' + transcript;
            });
          };
    
          recognition.onerror = (event) => {
            console.error('Speech recognition error:', event.error);
            setIsRecording(false);
          };
    
          recognition.onend = () => {
            setIsRecording(false);
          };
    
          setRecognition(recognition);
        }
      }, []);
    
      const toggleRecording = () => {
        if (!recognition) return;
    
        if (isRecording) {
          recognition.stop();
        } else {
          recognition.start();
          setIsRecording(true);
        }
      };

    return (
        <div className="from-indigo-900 via-purple-900 to-black text-white">
            <div className="max-w-4xl mx-auto px-4 py-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="relative">
                        <textarea
                            value={content}
                            onChange={handleContentChange}
                            // onChange={(e) => setContent(e.target.value)}
                            placeholder="Share your dream... Use @ for mentions and # for hashtags"
                            className="w-full p-4 bg-white/10 border border-purple-400/50 rounded-lg text-white placeholder-gray-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-500/30 transition-all"
                            rows="4"
                        />

                        {/* Tag Suggestions */}
                        {content.includes('#') && tagSuggestions.length > 0 && (
                            <div className="mt-2">
                            {tagSuggestions
                                .filter(tag => 
                                tag.toLowerCase().includes(
                                    content.match(/#(\w+)$/)?.[1]?.toLowerCase() || ''
                                )
                                )
                                .slice(0, 5)
                                .map(tag => (
                                    
                                <button
                                    type="button"
                                    key={tag}
                                    onClick={() => insertSuggestion(tag, 'tag')}
                                    className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs"
                                >
                                    <span 
                                        key={tag} 
                                        onClick={() => insertSuggestion(tag, 'tag')}
                                        className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs mr-2 cursor-pointer hover:bg-purple-200 transition-colors"
                                    >
                                    #{tag}
                                    </span>
                                </button>
                                ))
                            }
                            </div>
                        )}

                        {/* Mention Suggestions */}
                        {content.includes('@') && mentionSuggestions.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                            {mentionSuggestions
                                .filter(mention => 
                                mention.toLowerCase().includes(
                                    content.match(/@(\w+)$/)?.[1]?.toLowerCase() || ''
                                )
                                )
                                .slice(0, 5)
                                .map(mention => (
                                <button
                                    type="button"
                                    key={mention}
                                    onClick={() => insertSuggestion(mention, 'mention')}
                                    className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs mr-2 cursor-pointer hover:bg-blue-200 transition-colors"
                                    >
                                    @{mention}
                                </button>
                                ))
                            }
                            </div>
                        )}
            {/* Optional: Display extracted tags and mentions */}

                <div className="flex space-x-4">
                        {tags.length > 0 && (
                        <div>
                            <strong>Tags:</strong> 
                            {tags.map(tag => (
                            <span 
                                key={tag} 
                                className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs mr-2"
                            >
                                #{tag}
                            </span>
                            ))}
                        </div>
                        )}
                        {mentions.length > 0 && (
                        <div>
                            <strong>Mentions:</strong>
                            {mentions.map(mention => (
                            <span 
                                key={mention} 
                                className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs mr-2"
                            >
                                @{mention}
                            </span>
                            ))}
                        </div>
                        )}
                    </div>
                        <button
                            type="button"
                            onClick={toggleRecording}
                            className="absolute bottom-2 right-2 p-2 text-gray-500 hover:text-purple-500 dark:text-gray-400 dark:hover:text-purple-400 transition-colors"
                            title={isRecording ? 'Stop recording' : 'Start recording'}
                        >
                            {isRecording ? <FaStop className="w-5 h-5" /> : <FaMicrophone className="w-5 h-5" />}
                        </button>
                    </div>

                    <div className="space-y-6">
                        {/* Visibility Control */}
                        <div className="flex flex-col gap-2">
                            <label className="text-gray-300 text-sm">
                                Visibility
                            </label>
                            <select
                                value={visibility}
                                onChange={(e) => setVisibility(e.target.value)}
                                className="bg-white/20 border-purple-400/50 rounded-full text-white px-4 py-2 w-full max-w-xs focus:border-purple-400 focus:ring-2 focus:ring-purple-500/30 transition-all"
                            >
                                <option value="public">Public</option>
                                <option value="private">Private</option>
                            </select>
                        </div>

                        <ImageGenerationToggle 
                            isChecked={features.imageGeneration}
                            onToggle={toggleImageGeneration}
                            userCredits={userData.credits}
                            userRole={userData.role}
                        />

                        <AdGatedFeatures 
                            onFeatureSelect={handleFeatureSelect}
                            userType={userType}
                        />

                        {/* Art Style Selection - Only show when image generation is selected */}
                        {features.imageGeneration && (
                            <div className="flex flex-col gap-2">
                                <label className="text-gray-300 text-sm">
                                    Dream Style
                                </label>
                                <select
                                    value={artStyle}
                                    onChange={(e) => setArtStyle(e.target.value)}
                                    className="bg-white/20 border-purple-400/50 rounded-full text-white px-4 py-2 w-full focus:border-purple-400 focus:ring-2 focus:ring-purple-500/30 transition-all"
                                >
                                    <option value="">Random Style</option>
                                    {ART_STYLES.map((style) => (
                                        <option key={style.name} value={style.name}>
                                            {style.label}
                                        </option>
                                    ))}
                                </select>
                                {artStyle && (
                                    <p className="text-sm text-gray-400">
                                        Selected: {ART_STYLES.find(style => style.name === artStyle)?.label}
                                    </p>
                                )}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading || !content.trim()}
                            className="w-full py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-full font-medium hover:from-purple-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            {loading ? 'Posting...' : 'Publish Dream'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PostForm;